import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcMfrecoV1PositionDayQueryServicePositionDayGet } from '@interxfi/api-client/generated/hooks';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { DateFormat } from '@interxfi/design-system/components/format/date';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcMfrecoV1PositionDayState, IxcMutualfundsV1FundVenue } from '@interxfi/types';
import { AssetBreadcrumb } from '../../../../../../../../../../../../../features/asset/asset-breadcrumb';
import { AssetLogoBreadcrumb } from '../../../../../../../../../../../../../features/asset/logo/asset-logo-breadcrumb';
import { MfRecoPositionDayEntityMenu } from '../../../../../../../../../../../../../features/mutual-funds/reconciliation/position-day/mfreco-position-day-entity-menu';
import { MfRecoPositionDayEventSummary } from '../../../../../../../../../../../../../features/mutual-funds/reconciliation/position-day/mfreco-position-day-event';
import { getMfRecoPositionDayStateListItems } from '../../../../../../../../../../../../../features/mutual-funds/reconciliation/position-day/mfreco-position-day-list-items';
import { getMfRecoPositionDayDetailPath } from '../../../../../../../../../../../../../features/mutual-funds/reconciliation/position-day/mfreco-position-day-routing';
import { MfRecoPositionDayStatusPill } from '../../../../../../../../../../../../../features/mutual-funds/reconciliation/position-day/mfreco-position-day-status-pill';
import { getPageHead } from '../../../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/mutualfunds/reconciliation/position-day/_position-day/$venue/$date/$assetId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `mfreco-position-day-${params.assetId}-${params.date}-${params.venue}`,
        label: (
          <HorizontalStack blockAlign="center" gap={1} wrap="nowrap">
            <AssetBreadcrumb assetId={params.assetId} />
            <span>
              (<DateFormat value={params.date} wellKnownType="date_iso" />)
            </span>
          </HorizontalStack>
        ),
        prefix: <AssetLogoBreadcrumb assetId={params.assetId} />,
        to: getMfRecoPositionDayDetailPath(params.venue as IxcMutualfundsV1FundVenue, params.date, params.assetId),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Mutual Fund Position Day'),
  component: CnsAssetDayDetail,
});

function getListItems(data: IxcMfrecoV1PositionDayState | undefined) {
  const items = getMfRecoPositionDayStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [
          items.data.fund.cusip,
          items.data.fund.name,
          items.data.fund.symbol.formatted,
          ...(items.data.difference ? [items.data.difference] : []),
          ...(items.data.breakReason ? [items.data.breakReason] : []),
        ],
      },
      ...(items.data.ledger
        ? [
            {
              id: 'ledger',
              title: 'Ledger Position',
              items: [items.data.ledger.shares, items.data.ledger.asOf, items.data.ledger.accountCode],
            },
          ]
        : []),
      ...(items.data.venueReport
        ? [
            {
              id: 'venue',
              title: 'Venue Position',
              items: [
                items.data.venueReport.shares,
                items.data.venueReport.receivedAt,
                { ...items.data.venueReport.source.typeName, title: 'Source' },
                ...('jpmam' in items.data.venueReport.source ? [items.data.venueReport.source.jpmam.value.dataHash] : []),
              ],
            },
          ]
        : []),
    ] as const satisfies ReadonlyListGroup[],
    right: [items.assetId, items.date, ...(items.data.caseId ? [items.data.caseId] : [])] as const satisfies ReadonlyListItem[],
  } as const;
}

function CnsAssetDayDetail() {
  const { assetId, date, venue } = Route.useParams();
  const request = { assetId, date, venue: venue as IxcMutualfundsV1FundVenue } as const;
  const { data, isPending, error } = useIxcMfrecoV1PositionDayQueryServicePositionDayGet(request);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.positionDay), [data?.positionDay]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <MfRecoPositionDayStatusPill isPending={showPending} size="large" status={data?.positionDay?.status} />
          </HorizontalStack>

          <MfRecoPositionDayEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <MfRecoPositionDayEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
